// Web Speech API wrapper.
// Splits text into sentences so Chrome Android's ~15s TTS timeout is never hit.

let activeUtterance = null
let speakGeneration = 0

// Chrome loads voices asynchronously. This promise resolves once they're ready.
let voicesPromise = null
function getVoices() {
  if (!voicesPromise) {
    voicesPromise = new Promise(resolve => {
      const voices = speechSynthesis.getVoices()
      if (voices.length) { resolve(voices); return }
      // Timeout fallback — voiceschanged may never fire on some Android builds
      const timer = setTimeout(() => resolve([]), 3000)
      speechSynthesis.addEventListener('voiceschanged', () => {
        clearTimeout(timer)
        resolve(speechSynthesis.getVoices())
      }, { once: true })
    })
  }
  return voicesPromise
}
// Pre-warm: start loading voices immediately so they're ready when the user taps
if (typeof speechSynthesis !== 'undefined') getVoices()

function pickVoice(voices) {
  return voices.find(v => v.lang === 'en-US' && !v.name.includes('Compact'))
      || voices.find(v => v.lang.startsWith('en'))
      || voices[0]
      || null
}

function splitSentences(text) {
  return text.match(/[^.!?]*[.!?]*/g)?.map(s => s.trim()).filter(Boolean) ?? [text]
}

export async function speak(text, { onEnd, onError } = {}) {
  stop()

  const voices = await getVoices()
  const voice  = pickVoice(voices)
  const parts  = splitSentences(text)
  if (!parts.length) { onEnd?.(); return }

  const gen = ++speakGeneration
  let idx = 0

  function next() {
    if (speakGeneration !== gen) return   // stopped or superseded
    if (idx >= parts.length) {
      activeUtterance = null
      onEnd?.()
      return
    }

    // Always create a fresh utterance — reusing causes silent failures on Safari/Chrome.
    const utterance = new SpeechSynthesisUtterance(parts[idx++])
    utterance.voice = voice
    utterance.rate  = 1.0
    utterance.lang  = 'en-US'
    utterance.onend = next
    utterance.onerror = (e) => {
      if (e.error === 'interrupted' || e.error === 'canceled') return
      if (speakGeneration === gen) onError?.(e)
    }
    activeUtterance = utterance
    speechSynthesis.speak(utterance)
  }

  next()
}

export function stop() {
  speakGeneration++
  if (activeUtterance) {
    activeUtterance.onend   = null
    activeUtterance.onerror = null
    activeUtterance = null
  }
  speechSynthesis.cancel()
}

// ── text builders ───────────────────────────────────────────────────────────

// Full reading of a section (normal playback mode).
export function sectionText(section, questionNum) {
  switch (section.type) {
    case 'text':
      return `${section.title}. ${section.content}`
    case 'media':
      return `Image: ${section.title}. ${section.caption || 'No caption.'}`
    case 'question':
      if (section.kind === 'multiple_choice') {
        const opts = (section.options ?? [])
          .map(o => `Option ${o.id.toUpperCase()}: ${o.text}`)
          .join('. ')
        return `Question ${questionNum}: ${section.prompt}. ${opts}.`
      }
      return `Question ${questionNum}: ${section.prompt}`
    default:
      return ''
  }
}

// History mode: full section text, plus the user's response if any.
export function historyText(section, response, questionNum) {
  const base = sectionText(section, questionNum)
  if (!response?.transcript) return base
  const label = section.type === 'question' ? 'Your answer' : 'Your comment'
  return `${base}. ${label}: ${response.transcript}`
}

// Read-back after transcription: "You said: [transcript]"
export function readbackText(transcript) {
  return `You said: ${transcript}. Tap to re-record, or wait to confirm.`
}
